import { BrowserRouter, Routes, Route, Navigate } from 'react-router';
import * as Sentry from '@sentry/react';
import { useAuth0 } from '@auth0/auth0-react';
import { AppShell } from '@/components/layout/app-shell';
import { LoginPage } from '@/routes/login';
import { CallbackPage } from '@/routes/callback';
import { NotFoundPage } from '@/routes/not-found';
import { DashboardPage } from '@/routes/dashboard';
import { CoursesPage } from '@/routes/courses/index';
import { CourseDetailPage } from '@/routes/courses/detail';
import { CourseByCoursePage } from '@/routes/courses/by-course';
import { QuizAttemptPage } from '@/routes/quiz/attempt';
import { DocumentsPage } from '@/routes/documents/index';
import { ReadingsPage } from '@/routes/documents/readings';
import { CertificatesPage } from '@/routes/certificates/index';
import { NotificationsPage } from '@/routes/notifications/index';
import { CompetencyPage } from '@/routes/competency/index';
import { AssessmentDetailPage } from '@/routes/competency/assessment-detail';
import { CorrectiveActionDetailPage } from '@/routes/competency/corrective-detail';
import { ExternalTrainingsPage } from '@/routes/external-trainings/index';
import { LoadingSpinner } from '@/components/shared/loading-spinner';
import { useProfile } from '@/api/profile';
import { RequireRole } from '@/components/auth/require-role';
import { RequireInstructor } from '@/components/auth/require-instructor';
import { ActiveCompanyProvider } from '@/context/active-company-context';
// Instructor pages
import { QuizGradingPage } from '@/routes/instructor/quiz-grading';
import { EvidenceReviewPage } from '@/routes/instructor/evidence-review';
import { CompleteLessonsPage } from '@/routes/instructor/complete-lessons';
import { ReportsPage } from '@/routes/instructor/reports';
import { InstructorCorrectiveActionsPage } from '@/routes/instructor/corrective-actions';
import { EnrollmentsPage } from '@/routes/instructor/enrollments';
import { InstructorCoursesPage } from '@/routes/instructor/courses/index';
import { InstructorCourseDetailPage } from '@/routes/instructor/courses/detail';
import { ObservationsListPage } from '@/routes/instructor/observations';
import { ObservationDetailPage } from '@/routes/instructor/observation-detail';
// Supervisor pages
import { TeamPage } from '@/routes/supervisor/team';
import { ChangesPage } from '@/routes/supervisor/changes';
import { SupervisorAssessmentsPage } from '@/routes/supervisor/assessments';
import { DeclarationsPage } from '@/routes/supervisor/declarations';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth0();

  if (isLoading) return <LoadingSpinner className="min-h-screen" />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;

  return <>{children}</>;
}

function RequireActiveUser({ children }: { children: React.ReactNode }) {
  const { data: profile, isLoading, error } = useProfile();
  const { logout } = useAuth0();

  if (isLoading) return <LoadingSpinner className="min-h-screen" />;

  if (error || !profile) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background">
        <h1 className="text-2xl font-bold text-destructive">Acceso denegado</h1>
        <p className="text-sm text-muted-foreground">
          Tu cuenta no esta registrada en el sistema o ha sido desactivada.
        </p>
        <button
          onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
          className="rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground hover:bg-primary/90"
        >
          Cerrar sesión
        </button>
      </div>
    );
  }

  return <>{children}</>;
}

function SentryFallback() {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background">
      <h1 className="text-2xl font-bold text-destructive">Error inesperado</h1>
      <p className="text-sm text-muted-foreground">
        Ocurrió un error inesperado. Por favor recarga la página.
      </p>
      <button
        onClick={() => window.location.reload()}
        className="rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground hover:bg-primary/90"
      >
        Recargar
      </button>
    </div>
  );
}

export default function App() {
  return (
    <Sentry.ErrorBoundary fallback={SentryFallback}>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/callback" element={<CallbackPage />} />

          <Route
            element={
              <ProtectedRoute>
                <RequireActiveUser>
                  <ActiveCompanyProvider>
                    <AppShell />
                  </ActiveCompanyProvider>
                </RequireActiveUser>
              </ProtectedRoute>
            }
          >
            <Route index element={<DashboardPage />} />
            <Route path="courses" element={<CoursesPage />} />
            <Route path="courses/by-course/:courseId" element={<CourseByCoursePage />} />
            <Route path="courses/:id" element={<CourseDetailPage />} />
            <Route path="quiz/:quizId" element={<QuizAttemptPage />} />
            <Route path="documents" element={<DocumentsPage />} />
            <Route path="documents/readings" element={<ReadingsPage />} />
            <Route path="certificates" element={<CertificatesPage />} />
            <Route path="external-trainings" element={<ExternalTrainingsPage />} />
            <Route path="notifications" element={<NotificationsPage />} />
            <Route path="competency" element={<CompetencyPage />} />
            <Route path="competency/assessments/:id" element={<AssessmentDetailPage />} />
            <Route
              path="competency/corrective-actions/:id"
              element={<CorrectiveActionDetailPage />}
            />
            {/* Instructor routes — gated by instructor capability, not the role
                ladder (Bug #2): a pure supervisor is not an instructor. */}
            <Route element={<RequireInstructor />}>
              <Route path="instructor/courses" element={<InstructorCoursesPage />} />
              <Route path="instructor/courses/:id" element={<InstructorCourseDetailPage />} />
              <Route path="instructor/enrollments" element={<EnrollmentsPage />} />
              <Route path="instructor/quiz-grading" element={<QuizGradingPage />} />
              <Route path="instructor/evidence-review" element={<EvidenceReviewPage />} />
              <Route path="instructor/complete-lessons" element={<CompleteLessonsPage />} />
              <Route path="instructor/observations" element={<ObservationsListPage />} />
              <Route path="instructor/observations/:id" element={<ObservationDetailPage />} />
              <Route path="instructor/reports" element={<ReportsPage />} />
              <Route
                path="instructor/corrective-actions"
                element={<InstructorCorrectiveActionsPage />}
              />
            </Route>

            {/* Supervisor routes */}
            <Route element={<RequireRole minRole="supervisor" />}>
              <Route path="supervisor/team" element={<TeamPage />} />
              <Route path="supervisor/changes" element={<ChangesPage />} />
              <Route path="supervisor/assessments" element={<SupervisorAssessmentsPage />} />
              <Route path="supervisor/declarations" element={<DeclarationsPage />} />
            </Route>

            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </Sentry.ErrorBoundary>
  );
}
